import * as React from "react";
import { SecondaryButton } from "@graffer-inc/console-design-kit";
import { IFormSchemaV2 } from "@graffer-inc/form-schema";
import { TabLiterals } from "@graffer-inc/form-builder";

import { useEffect, useState } from "react";
import { useModal } from "../../../../../utils/custom-hooks";
import { BaseModal } from "../../../../atoms/base-modal";
import {
  ProcedureDetailType,
  ProcedureEntity,
} from "../../../../../application/domain/procedures/procedure-entity";
import { RBACButton } from "../../../../molecules/rbac-button";
import {
  CloudGovFormSchema,
  SmartApplyFormSchemaValidator,
  ApplicantSchemaKeysMigrator,
} from "@graffer-inc/cloud-gov-core";
import { CloudGovFormEditor } from "../../form-editor/form-editor";
import {
  parseQueryOfLocation,
  rebuildQuery,
} from "../../../../../utils/url-utils";
import { useHistory, useLocation } from "react-router";
import { UpdateButton } from "../update-button";
import { reportUnexpectedError } from "@graffer-inc/smart-apply";
import { FormPreview } from "./form-preview";
import Analytics from "../../../../../common/analytics";

type FormEditProps = {
  procedure: ProcedureEntity;
  onDraftUpdate: (draft: ProcedureDetailType) => void;
  setIsFormUnsaved: (isFormUnsaved: boolean) => void;
  allowCustomElemId?: boolean;
};

export const FormEditPage: React.FC<FormEditProps> = ({
  procedure,
  onDraftUpdate,
  setIsFormUnsaved,
  allowCustomElemId,
}) => {
  const history = useHistory();

  const { sectionIndex = "0", formTab } = parseQueryOfLocation(
    useLocation(),
  ) as {
    sectionIndex: string;
    formTab?: TabLiterals;
  };
  const [initialSectionIndex] = useState(parseInt(sectionIndex, 10));

  const [currentFormSchema, setCurrentFormSchema] = useState<IFormSchemaV2>(
    procedure.getLiveDetail().template.formSchema,
  );
  const handleFormChange = React.useCallback(
    (formSchema: IFormSchemaV2) => {
      setCurrentFormSchema(formSchema);
      if (procedure.isDraft()) {
        setIsFormUnsaved(true);
      }
    },
    [setIsFormUnsaved, procedure],
  );
  const [validForm, setValidForm] = useState<boolean>(false);

  const previewModal = useModal();
  const templateMasterModal = useModal();
  const importExportModal = useModal();

  const updateProcedure = async () => {
    try {
      // 設定自由化時に再度修正。
      const migrateRes = ApplicantSchemaKeysMigrator.up(currentFormSchema);
      if (migrateRes instanceof Error) {
        throw migrateRes;
      }

      const detail = procedure.getLiveDetail();
      onDraftUpdate({
        ...detail,
        template: {
          ...detail.template,
          formSchema: currentFormSchema,
        },
        applicantSchemaKeys: migrateRes,
      });
      setIsFormUnsaved(false);
      Analytics.updateProcedure({ id: procedure.id, name: procedure.name });
    } catch (e) {
      reportUnexpectedError(e);
    }
  };

  useEffect(() => {
    const validateErr = new SmartApplyFormSchemaValidator(
      currentFormSchema,
      procedure.getLiveDetail().applicantSchemaKeys,
    ).validate({
      withJPKI: procedure.requireJPKISignature(),
      withMOJ: procedure.requireMOJSignature(),
    }).length;
    setValidForm(validateErr > 0);
    if (procedure.isPublished()) {
      Analytics.updatePublishedProcedureFormSchema({
        id: procedure.id,
        name: procedure.name,
      });
    } else {
      Analytics.updateProcedureFormSchema({
        id: procedure.id,
        name: procedure.name,
      });
    }
  }, [currentFormSchema, procedure]);

  const validateOpt = React.useMemo(() => {
    return {
      withJPKI: procedure.requireJPKISignature(),
      withMOJ: procedure.requireMOJSignature(),
    };
  }, [procedure]);

  const onChangeCurrentSection = React.useCallback(
    (sectIndex: number) => {
      history.replace(
        rebuildQuery({
          sectionIndex: sectIndex.toString(10),
        }),
      );
    },
    [history],
  );

  const onChangeCurrentTab = React.useCallback(
    (tab: "FORM" | "RULE" | "FORMULA") =>
      history.replace(
        rebuildQuery({
          formTab: tab,
        }),
      ),
    [history],
  );

  return (
    <>
      <div className="columns">
        <div className="column has-text-left">
          <div className="columns is-vcentered">
            <div className="column has-text-right">
              <div className="level-right buttons">
                <SecondaryButton
                  text={"プレビュー"}
                  onClick={previewModal.open}
                />
                <RBACButton
                  requirePermissions={"procedures.import_export_formschema"}
                  onClick={importExportModal.open}
                  buttonText={"Import/Export"}
                  buttonTypes={"secondary"}
                />
                <UpdateButton
                  procedure={procedure}
                  onClick={updateProcedure}
                  canModifyAfterPublish={true}
                  disabled={validForm}
                />
              </div>
            </div>
          </div>
          {
            // フォームマスタから取り込むときにFormEditorコンポーネントを再初期化しないと、外部から新しい状態を渡せないため、modalの状態でtoggleさせる
            !templateMasterModal.isActive && !importExportModal.isActive && (
              <>
                <CloudGovFormEditor
                  allowCustomElemId={allowCustomElemId}
                  initialSchema={currentFormSchema}
                  initialSection={initialSectionIndex}
                  initialTab={formTab}
                  validateOpt={validateOpt}
                  onChangeCurrentSection={onChangeCurrentSection}
                  currentFormSchema={currentFormSchema}
                  onChange={handleFormChange}
                  onChangeCurrentTab={onChangeCurrentTab}
                  isSafeEditOnly={procedure.isPublished()}
                  applicantSchemaKeys={
                    procedure.getLiveDetail().applicantSchemaKeys
                  }
                />
              </>
            )
          }
        </div>
      </div>
      <BaseModal
        title={"フォームプレビュー"}
        isActive={previewModal.isActive}
        onClose={previewModal.close}
      >
        <section className="modal-card-body">
          {previewModal.isActive && (
            <FormPreview
              currentFormSchema={currentFormSchema}
              applicantSchemaKeys={
                procedure.getLiveDetail().applicantSchemaKeys
              }
            />
          )}
        </section>
        <section className="modal-card-foot">
          <button
            type="button"
            className="button is-fullwidth"
            onClick={previewModal.close}
          >
            閉じる
          </button>
        </section>
      </BaseModal>
      <BaseModal
        title={"Import/Export"}
        isActive={importExportModal.isActive}
        onClose={importExportModal.close}
      >
        <section className="modal-card-body">
          <div className="field">
            <textarea
              className="textarea"
              value={JSON.stringify(currentFormSchema)}
              onChange={(e) => {
                try {
                  setCurrentFormSchema(
                    CloudGovFormSchema.fromJSON(JSON.parse(e.target.value)),
                  );
                } catch (e) {
                  alert(e.message);
                }
              }}
            />
          </div>
        </section>
        <section className="modal-card-foot">
          <button
            type="button"
            className="button is-failure"
            onClick={importExportModal.close}
          >
            閉じる
          </button>
        </section>
      </BaseModal>
    </>
  );
};
